import {idFromGlobalId} from '../../../../../util/GlobalIdUtil';
import type {BackendEntityId, PossibleEntityId, TimeRegistrationEntityId} from '../types/TimeRegistrationEntityId';
import {TimeRegistrationEntity} from '../types/TimeRegistrationEntityId';

import type {TimeRegistrationModal_entity, TimeRegistrationModal_entityProject} from '../types/TimeRegistrationModalEntity';

export function toEntityId({taskId, projectId, idleTimeId}: PossibleEntityId): TimeRegistrationEntityId | null {
	if (taskId) {
		return {entity: TimeRegistrationEntity.Task, id: idFromGlobalId(taskId)};
	}
	if (projectId) {
		return {entity: TimeRegistrationEntity.Project, id: idFromGlobalId(projectId)};
	}
	if (idleTimeId) {
		return {entity: TimeRegistrationEntity.IdleTime, id: idFromGlobalId(idleTimeId)};
	}

	return null;
}

export function toBackendEntityId(
	entityId: TimeRegistrationEntityId,
	projectId: string | undefined
): BackendEntityId | Record<string, never> {
	switch (entityId.entity) {
		case TimeRegistrationEntity.Task:
			return {taskId: entityId.id, projectId: projectId ? idFromGlobalId(projectId) : null, idleTimeId: null};
		case TimeRegistrationEntity.Project:
			return {taskId: null, projectId: entityId.id, idleTimeId: null};
		case TimeRegistrationEntity.IdleTime:
			return {taskId: null, projectId: null, idleTimeId: entityId.id};
		default:
			return {};
	}
}

type SearchResultToBackendEntityIdInput = {
	id: number;
	projectId: number | null;
	variant: string;
};

export function searchResultToBackendEntityId({
	id,
	projectId,
	variant,
}: SearchResultToBackendEntityIdInput): BackendEntityId | Record<string, never> {
	switch (variant) {
		case 'TASK':
			return {taskId: id, projectId: projectId, idleTimeId: null};
		case 'PROJECT':
			return {taskId: null, projectId: id, idleTimeId: null};
		case 'INTERNAL_TIME':
			return {taskId: null, projectId: null, idleTimeId: id};
		default:
			return {};
	}
}

export function isTaskEntity(entity: TimeRegistrationEntity): boolean {
	return entity === TimeRegistrationEntity.Task;
}

export function isProjectEntity(entity: TimeRegistrationEntity): boolean {
	return entity === TimeRegistrationEntity.Project;
}

export function getEntityProject(
	targetEntity: TimeRegistrationModal_entity | null
): TimeRegistrationModal_entityProject | null {
	switch (targetEntity?.entity) {
		case TimeRegistrationEntity.Task:
			return targetEntity.project;
		case TimeRegistrationEntity.Project:
			return targetEntity;
		default:
			return null;
	}
}

export function isTargetEntityBillable(targetEntity: TimeRegistrationModal_entity | null): boolean {
	// billable === true is the default state
	if (!targetEntity) return true;

	switch (targetEntity.entity) {
		case TimeRegistrationEntity.Task:
		case TimeRegistrationEntity.Project:
			return targetEntity.billable ?? true;
		case TimeRegistrationEntity.IdleTime:
			return false;
	}
}

export function doesTargetEntityAllowRoles(targetEntity: TimeRegistrationModal_entity | null): boolean {
	// roles === true is the default state
	if (!targetEntity) return true;

	switch (targetEntity.entity) {
		case TimeRegistrationEntity.Task:
		case TimeRegistrationEntity.Project:
			return true;
		case TimeRegistrationEntity.IdleTime:
			return false;
	}
}
